// src/config/targets.ts — the target catalogue of SPEC §3.8.1, as data.
// Distances metres, speeds m/s, angles degrees at this boundary only.

/**
 * What can be attacked, and what it takes (SPEC §3.8.1).
 *
 * Each entry carries an aim point, a hardness, and — for two of them — a *requirement* that the
 * attack itself must satisfy. Those requirements are the reason the loadout screen at M10 has
 * teeth: a penetrator warhead forces a steep terminal dive, a steep dive forces altitude, and
 * altitude forces exposure to everything M5 put in the theatre. Good mission design exploits that
 * chain, so the gates have to be real rather than cosmetic.
 */

export const TargetKind = {
  RadarAntenna: 'RADAR_ANTENNA',
  SamLauncher: 'SAM_LAUNCHER',
  FuelStorage: 'FUEL_STORAGE',
  AmmoBunker: 'AMMO_BUNKER',
  CommandPost: 'COMMAND_POST',
  NavalVessel: 'NAVAL_VESSEL',
  BridgeSpan: 'BRIDGE_SPAN',
  CommsMast: 'COMMS_MAST',
} as const;
export type TargetKindId = (typeof TargetKind)[keyof typeof TargetKind];

export interface TargetParams {
  readonly id: TargetKindId;
  readonly displayName: string;

  /**
   * Radius within which an impact counts as striking the target at all, metres. Beyond it the
   * attack is a MISS however good the run was — this is the target's physical size, not a
   * generosity setting.
   */
  readonly bodyRadiusM: number;
  /**
   * Distance from the aim point inside which a hit is graded DIRECT, metres. SPEC §3.8.2 fixes
   * this at 2 m; a few targets are large enough to warrant more.
   */
  readonly directHitRadiusM: number;

  /**
   * 0 (soft) to 1 (hardened). Scales the damage an impact delivers — a command post shrugs off
   * what flattens a truck.
   */
  readonly hardness: number;

  /** Minimum impact speed for the warhead to function, m/s. Zero means any impact works. */
  readonly minImpactSpeedMs: number;
  /**
   * Minimum dive angle, degrees below horizontal, for the attack to defeat the target's
   * protection. Zero means the target can be struck from any angle.
   */
  readonly minDiveAngleDeg: number;
  /** True when only a penetrator warhead can satisfy the requirement. */
  readonly requiresPenetrator: boolean;

  /** Height of the aim point above the target's base, metres. */
  readonly aimPointHeightM: number;
  /** Secondary explosion: sustained fire after destruction, seconds. Zero means none. */
  readonly secondaryFireS: number;
  /** Score awarded for a destroying hit. Scaled by grade at M8. */
  readonly scoreValue: number;
}

const TARGET_LIST: readonly TargetParams[] = [
  {
    id: TargetKind.RadarAntenna,
    displayName: 'RADAR ANTENNA',
    // Fragile, small, and hard to hit — the difficulty is the geometry, not the warhead.
    bodyRadiusM: 4.5,
    directHitRadiusM: 2,
    hardness: 0.05,
    minImpactSpeedMs: 0,
    minDiveAngleDeg: 0,
    requiresPenetrator: false,
    aimPointHeightM: 5,
    secondaryFireS: 0,
    scoreValue: 120,
  },
  {
    id: TargetKind.SamLauncher,
    displayName: 'SAM LAUNCHER',
    bodyRadiusM: 5,
    directHitRadiusM: 2,
    hardness: 0.2,
    minImpactSpeedMs: 0,
    minDiveAngleDeg: 0,
    requiresPenetrator: false,
    aimPointHeightM: 1.6,
    // Rounds still on the rails: a launcher goes up spectacularly.
    secondaryFireS: 14,
    scoreValue: 150,
  },
  {
    id: TargetKind.FuelStorage,
    displayName: 'FUEL STORAGE',
    // Large and forgiving to hit; the spectacle is the point.
    bodyRadiusM: 11,
    directHitRadiusM: 4,
    hardness: 0.1,
    minImpactSpeedMs: 0,
    minDiveAngleDeg: 0,
    requiresPenetrator: false,
    aimPointHeightM: 4,
    secondaryFireS: 28,
    scoreValue: 130,
  },
  {
    id: TargetKind.AmmoBunker,
    displayName: 'AMMO BUNKER',
    /**
     * The hard setup, and the one the whole loadout tension is built around: a buried roof that
     * only a penetrator arriving steeply can defeat. A shallow run puts the warhead into a metre
     * of reinforced concrete and achieves nothing.
     */
    bodyRadiusM: 9,
    directHitRadiusM: 3,
    hardness: 0.85,
    minImpactSpeedMs: 90,
    minDiveAngleDeg: 40,
    requiresPenetrator: true,
    aimPointHeightM: 2.5,
    secondaryFireS: 22,
    scoreValue: 260,
  },
  {
    id: TargetKind.CommandPost,
    displayName: 'COMMAND POST',
    bodyRadiusM: 8,
    directHitRadiusM: 2.5,
    hardness: 0.6,
    minImpactSpeedMs: 60,
    minDiveAngleDeg: 0,
    requiresPenetrator: false,
    aimPointHeightM: 4,
    secondaryFireS: 8,
    scoreValue: 200,
  },
  {
    id: TargetKind.NavalVessel,
    displayName: 'NAVAL VESSEL',
    bodyRadiusM: 14,
    directHitRadiusM: 3,
    hardness: 0.45,
    // A slow impact bounces off a hull; the waterline needs energy behind it.
    minImpactSpeedMs: 60,
    minDiveAngleDeg: 0,
    requiresPenetrator: false,
    aimPointHeightM: 3,
    secondaryFireS: 18,
    scoreValue: 220,
  },
  {
    id: TargetKind.BridgeSpan,
    displayName: 'BRIDGE SPAN',
    // The precision showcase: a wide structure with a narrow point that actually matters.
    bodyRadiusM: 7,
    directHitRadiusM: 2,
    hardness: 0.7,
    minImpactSpeedMs: 70,
    minDiveAngleDeg: 0,
    requiresPenetrator: true,
    aimPointHeightM: 1,
    secondaryFireS: 0,
    scoreValue: 240,
  },
  {
    id: TargetKind.CommsMast,
    displayName: 'COMMS MAST',
    // Thin, and genuinely difficult — a lattice offers almost nothing to hit.
    bodyRadiusM: 2.5,
    directHitRadiusM: 1.5,
    hardness: 0.05,
    minImpactSpeedMs: 0,
    minDiveAngleDeg: 0,
    requiresPenetrator: false,
    aimPointHeightM: 2,
    secondaryFireS: 0,
    scoreValue: 110,
  },
];

export const TARGETS: Record<TargetKindId, TargetParams> = Object.fromEntries(
  TARGET_LIST.map((target) => [target.id, target]),
) as Record<TargetKindId, TargetParams>;

/** Warhead types (SPEC §3.7). Chosen at brief; constrains how a target must be attacked. */
export const WarheadKind = {
  Fragmentation: 'FRAGMENTATION',
  Penetrator: 'PENETRATOR',
} as const;
export type WarheadKindId = (typeof WarheadKind)[keyof typeof WarheadKind];

export interface WarheadParams {
  readonly id: WarheadKindId;
  readonly displayName: string;
  readonly yield: number;
  /** Multiplier against hardened targets — the reason a penetrator exists. */
  readonly penetration: number;
  /** Radius over which the effect is spread, metres. Wide for frag, narrow for a penetrator. */
  readonly effectRadiusM: number;
}

export const WARHEADS: Record<WarheadKindId, WarheadParams> = {
  [WarheadKind.Fragmentation]: {
    id: WarheadKind.Fragmentation,
    displayName: 'FRAGMENTATION',
    yield: 1.0,
    penetration: 0.35,
    effectRadiusM: 14,
  },
  [WarheadKind.Penetrator]: {
    id: WarheadKind.Penetrator,
    displayName: 'PENETRATOR',
    yield: 1.15,
    penetration: 1.0,
    effectRadiusM: 5,
  },
};

/** One placed target. Missions supply these at M8. */
export interface TargetPlacement {
  readonly designation: string;
  readonly kind: TargetKindId;
  readonly xM: number;
  readonly zM: number;
  /** Heading the target faces, degrees. Only matters for the ones with a long axis. */
  readonly headingDeg?: number;
}

/**
 * Placeholder *data* until missions arrive at M8 — the same arrangement the threat laydown uses.
 * Placed on real features of the Kerani theatre so every target type can actually be flown at.
 */
export const DEMO_TARGET_LAYOUT: readonly TargetPlacement[] = [
  { designation: 'T-1', kind: TargetKind.FuelStorage, xM: 4400, zM: 1050 },
  { designation: 'T-2', kind: TargetKind.RadarAntenna, xM: 4180, zM: 760 },
  { designation: 'T-3', kind: TargetKind.AmmoBunker, xM: 4620, zM: 980 },
  { designation: 'T-4', kind: TargetKind.CommsMast, xM: 4050, zM: 1180 },
  { designation: 'T-5', kind: TargetKind.CommandPost, xM: 4320, zM: 1320, headingDeg: 30 },
  { designation: 'T-6', kind: TargetKind.SamLauncher, xM: 4700, zM: 1260 },
  // In the bay, where a water plume is worth seeing.
  { designation: 'T-7', kind: TargetKind.NavalVessel, xM: 3600, zM: 2050, headingDeg: 75 },
];
